import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { SocialIcon } from '@/components';
import { PageSeo } from '@/components';
import config from '@/config';
import { API } from '@/core/api';

export default function About() {
	const skills = [
		{ name: 'JavaScript', value: 85, main: true },
		{ name: 'React', value: 75, main: true },
		{ name: 'NextJS', value: 60, main: true },
		{ name: 'Angular', value: 75, main: true },
		{ name: 'Redux / MobX', value: 80, main: true },
		{ name: 'GraphQL', value: 70, main: true },
		{ name: 'ExtJS', value: 85, main: true },
		{ name: 'Node.js', value: 70, main: true },
		{ name: 'NestJS', value: 50 },
		{ name: 'ExpressJS', value: 70 },
		{ name: 'TypeScript', value: 70 },
		{ name: 'Jest', value: 70 },
		{ name: 'Git', value: 70 },
		{ name: 'Webpack', value: 50 },
		{ name: 'PHP', value: 60 },
		{ name: 'Python', value: 40 },
		{ name: 'Go', value: 30 },
		{ name: 'MySQL', value: 80 },
		{ name: 'MongoDB', value: 70 },
		{ name: 'Design Database', value: 70 },
		{ name: 'Sass/Scss', value: 70 },
		{ name: 'Tailwind', value: 70 },
		{ name: 'Bootstrap', value: 70 },
		{ name: 'Inkscape / Figma', value: 70 },
		{ name: 'Websocket', value: 70 },
		{ name: 'WebRTC', value: 70 },
		{ name: 'AWS', value: 50 },
		{ name: 'Linux Sysadmin', value: 50 },
	];

	const { t } = useTranslation('common');
	const mainSkills = skills.filter((skill) => skill.main);
	const additionalSkills = skills.filter((skill) => !skill.main);
	const { about } = config;

	return (
		<>
			<PageSeo
				title={`${t('about')} - ${config.author}`}
				description={`About me - ${config.author}`}
				url={`${config.siteUrl}/about`}
			/>
			<div className='divide-y'>
				<div className='pt-6 pb-8 space-y-2 md:space-y-5'>
					<h1 className='text-3xl font-extrabold leading-9 tracking-tight text-gray-900  dark:text-gray-200  sm:text-4xl sm:leading-10 md:text-6xl md:leading-14'>
						{t('about')}
					</h1>
				</div>
				<div className='items-start space-y-2 xl:grid xl:grid-cols-3 xl:gap-x-8 xl:space-y-0'>
					<div className='flex flex-col items-center pt-8 space-x-2'>
						<img src={config.image} alt='avatar' className='w-48 h-48 rounded-full' />
						<h3 className='pt-4 pb-2 text-2xl font-bold leading-8 tracking-tight'>
							{config.author}
						</h3>
						<div className='text-gray-500 dark:text-gray-400'>
							Full-stack web developer and freelancer
						</div>
						<div className='text-gray-500 dark:text-gray-400'>Kiev, Ukraine</div>
						<div className='text-gray-500 dark:text-gray-400'>{config.email}</div>
						<div className='flex pt-6 space-x-3'>
							<SocialIcon kind='mail' href={`mailto:${config.email}`} />
							<SocialIcon kind='github' href={config.github} />
							<SocialIcon kind='facebook' href={config.facebook} />
							<SocialIcon kind='youtube' href={config.youtube} />
							<SocialIcon kind='linkedin' href={config.linkedin} />
							<SocialIcon kind='twitter' href={config.twitter} />
						</div>

						<div className='p-6 w-full'>
							{mainSkills.map(({ name, value, main }) => (
								<div className='relative pt-1' key={name}>
									<div className='flex mb-2 items-center justify-between'>
										<div>
											<span className='text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-amber-600 bg-amber-200'>
												{name}
											</span>
										</div>
										<div className='text-right'>
											<span className='text-xs font-semibold inline-block text-amber-600'>
												{value}%
											</span>
										</div>
									</div>
									<div className='overflow-hidden h-2 mb-4 text-xs flex rounded bg-amber-200'>
										<div
											style={{ width: `${value}%` }}
											className='shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500'
										></div>
									</div>
								</div>
							))}
							<ul className='list-none'>
								<li className='mb-6 flex flex-wrap'>
									{additionalSkills.map(({ name, value }) => (
										<span
											key={name}
											className='mr-2 my-1 rounded-full border px-4 text-sm py-2 font-medium bg-green-600 dark:border-transparent  text-white'
										>
											{name}
										</span>
									))}
								</li>
							</ul>
						</div>
						<div className='mb-6 font-light text-base px-6 w-full'>
							<h4 className='text-base font-semibold mb-4'>{t('about-education')}</h4>
							<p>University of KEI KNEU - 2005 - 2010</p>
						</div>
						<div className='mb-6 font-light text-base px-6 w-full'>
							<h4 className='text-base font-semibold mb-4'>{t('about-languages')}</h4>
							<p>English - B1-B2</p>
							<p>German - A1</p>
						</div>
						<div className='mb-6 font-light text-base px-6 w-full'>
							<h4 className='text-base font-semibold mb-4'>{t('about-interests')}</h4>
							<ul>
								<li>Machine Learning</li>
								<li>Robotics</li>
								<li>DIY</li>
								<li>Snowboarding</li>
								<li>Solar energy producing</li>
							</ul>
						</div>
					</div>
					<div className='pt-8 pb-8 prose dark:prose-dark max-w-none xl:col-span-2 dark:text-gray-100'>
						<div id='profile' className='prose'>
							<h2 className='section-headline'>{t('about-profile')}</h2>
							{about.profile.map((paragraph) => (
								<p key={paragraph} className='text-gray-900 dark:text-gray-100'>
									{paragraph}
								</p>
							))}
						</div>
						<div>
							<hr className='mt-8 mb-12' />
							<div id='experience' className='prose'>
								<h2 className='section-headline text-gray-900 dark:text-gray-100'>
									{t('about-experience')}
								</h2>
								{about.experience.map(
									({ companyName, position, period, task, description, tools }) => (
										<div className='text-gray-900 dark:text-gray-100' key={companyName}>
											<h3>{companyName}</h3>
											<section className='mb-6'>
												<div className='lg:inline-block lg:w-3/12 lg:align-top italic mb-2'>
													{period}
												</div>
												<div className='lg:inline-block lg:w-8/12 font-semibold w-full'>
													{position}
												</div>
											</section>
											<section className='mb-6'>
												<div className='lg:inline-block lg:w-2/12 lg:align-top font-semibold mb-2'>
													{t('about-task')}:
												</div>
												<div className='lg:inline-block lg:w-9/12 w-full'>
													<ul>
														{task.map((task) => (
															<li key={task}>{task}</li>
														))}
													</ul>
												</div>
											</section>
											<section className='mb-6'>
												<div className='lg:inline-block lg:w-3/12 lg:align-top font-semibold mb-2'>
													{t('about-description')}:
												</div>
												<div className='lg:inline-block lg:w-8/12 w-full'>{description}</div>
											</section>
											<section className='mb-6'>
												<div className='lg:inline-block lg:w-2/12 lg:align-top font-semibold mb-2'>
													{t('about-tools')}:
												</div>
												<div className='lg:inline-block lg:w-9/12 w-full'>
													<div className='mb-6 p-0 pl-0 list-none  flex flex-wrap'>
														{tools.map((tool) => (
															<span
																key={tool}
																className='mr-2 my-1 rounded-full border px-4 text-sm py-2 font-medium bg-gray-400 dark:border-transparent text-white'
															>
																{tool}
															</span>
														))}
													</div>
												</div>
											</section>
										</div>
									)
								)}
							</div>

							<div>
								<hr className='mt-8 mb-12 text-gray-900 dark:text-gray-100' />
								<div id='projects' className='prose'>
									<h2 className='section-headline'>Expand my skills</h2>
									<div className='mb-16 text-gray-900 dark:text-gray-100'>
										<h4 id='expand-knowledge-headline'>
											I'd like to expand my skills and knowledge in several fields
										</h4>
										<ul>
											<li>Modern Web Game Development</li>
											<li>Testing, e.g. with Mocha, Chai, Jest</li>
											<li>Prototyping and Wireframing, e.g. with Figma, Adobe XD</li>
											<li>Scalable Vector Graphics</li>
											<li>Databases</li>
											<li>Mobile development</li>
											<li>
												Backend development, e.g. learn additional language like Go, Python, PHP,
												Java
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

export const getStaticProps = async ({ locale }) => {
	const apiRef = new API();
	const posts = await apiRef.getPosts();
	return {
		props: {
			...(await serverSideTranslations(locale, ['common'])),
		},
		revalidate: 1,
	};
};
